import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import * as css from './alpTable.module.scss';
import Icon, { IconType } from '../icon';
import { TextInput } from '../input';
import { roundToNearestEvenNumber } from '../../util/numberutils';
import { useFormatMessageFor } from '../../i18n/translation';
import translations from './alpTable.i18n';

/* TABLE COMMON PROPS */

interface TableProps {
  className?: string;
  id?: string;
  children?: ReactNode | null;
}

/* TABLE WRAPPER PROPS */

interface TableWrapperInterface extends TableProps {
  tableWrapperOnScroll?: (scrollTop: number) => void;
  tableWrapperClassName?: string;
  maxNoElements?: number;
  getInitTableWrapperScroll?: () => number;
}

interface TableWrapperPropsWithBlankOverflow extends TableWrapperInterface {
  numHeaders: number;
  blankOverflow: boolean;
  tableMainScrollTop: number;
}

interface TableWrapperPropsWithoutBlankOverflow extends TableWrapperInterface {
  numHeaders?: never;
  blankOverflow?: never;
  tableMainScrollTop?: never;
}

type TableWrapperProps =
  | TableWrapperPropsWithBlankOverflow
  | TableWrapperPropsWithoutBlankOverflow;

/* TABLE CELL PROPS */

interface TableCellProps extends TableProps {
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify' | 'start' | 'end';
}

interface TableTopCellNoSearch {
  search?: never;
  canSearch?: never;
}

interface TableTopCellSearch {
  search?: (col: number, value: string) => void;
  canSearch?: boolean;
}

type TableTopCellSearchType = TableTopCellNoSearch | TableTopCellSearch;

interface TableTopCellProps
  extends TableCellProps,
    Omit<React.ThHTMLAttributes<HTMLTableCellElement>, 'align'> {
  sort?: (col: number, ascending: boolean) => void;
  activeCol?: boolean;
  setActiveCol?: (col: number) => void;
  colIndex?: number;
  canSort?: boolean;
  initAscending?: boolean;
}

interface TableCellTdProps
  extends TableCellProps,
    Omit<React.TdHTMLAttributes<HTMLTableCellElement>, 'align'> {
  header?: string;
}

type TableCellThProps = TableTopCellProps & TableTopCellSearchType;

/* TABLE ROW PROPS */

interface TableTopRowProps
  extends TableProps,
    React.TableHTMLAttributes<HTMLTableRowElement> {
  top: true;
  noActiveHeaders?: boolean;
  rowBackground?: never;
}

export const RowBackground = {
  default: 'default',
  sky: 'sky',
  shade: 'shade',
  alert: 'alert',
  deactivated: 'deactivated',
} as const;

interface TableInnerRowProps
  extends TableProps,
    React.TableHTMLAttributes<HTMLTableRowElement> {
  top?: false;
  rowBackground?: keyof typeof RowBackground;
  noActiveHeaders?: never;
}

type TableRowType = TableTopRowProps | TableInnerRowProps;

/* TABLE CONTEXT AND IT'S PROPS */

interface TableContextCommonProps {
  breakTable: boolean;
  maxHeight: number;
  tableMainScrollTop: number;
}

interface TableContextWithBlankOverflow extends TableContextCommonProps {
  numHeaders: number;
  blankOverflow: boolean;
}

interface TableContextWithoutBlankOverflow extends TableContextCommonProps {
  numHeaders?: never;
  blankOverflow?: never;
}

type TableContextProps =
  | TableContextWithBlankOverflow
  | TableContextWithoutBlankOverflow;

const initTableContext: TableContextProps = {
  breakTable: false,
  maxHeight: 0,
  tableMainScrollTop: 0,
};

const TableContext = createContext<TableContextProps>(initTableContext);

/* GLOBAL TABLE VARIABLES */

const MAX_NUM_ELEMENTS = 15; // 15 (rows) is the specified height overflow breakpoint
const DEFAULT_CELL_HEIGHT = 56; // Default height of table cells, when table is not broken
const DEFAULT_CELL_HEIGHT_BROKEN_TABLE = 40; // Default height of table cells, when table is broken
const WIDTH_BREAKPOINT = 700; // Between mobile and tablet width

/* TABLE METHODS */

const getMaxHeightFrom = (maxNoElements: number, numHeaders?: number) =>
  (maxNoElements + 1) *
  (numHeaders
    ? DEFAULT_CELL_HEIGHT_BROKEN_TABLE * numHeaders
    : DEFAULT_CELL_HEIGHT);

const getMaxHeightForTable = (
  maxNoElements?: number,
  numHeaders?: number,
): number => {
  const maxHeight = getMaxHeightFrom(
    maxNoElements || MAX_NUM_ELEMENTS,
    numHeaders,
  );
  const windowHeight = window.innerHeight * 0.7;

  if (maxHeight > windowHeight) {
    return windowHeight;
  }

  return maxHeight;
};

/* REACT COMPONENTS */

const shouldUseMobileView = (
  tableWidth: number,
  tableWrapperWidth: number,
  breakpoint: React.MutableRefObject<number | undefined>,
): boolean => {
  if (tableWidth <= WIDTH_BREAKPOINT) return true;

  if (breakpoint.current) {
    if (window.innerWidth <= breakpoint.current) return true;

    breakpoint.current = undefined;
    return false;
  }

  if (tableWidth > tableWrapperWidth) {
    breakpoint.current = tableWidth - tableWrapperWidth + window.innerWidth;

    return true;
  }

  return false;
};

type useShouldBreakTableReturnFunc = (
  tableWidth: number,
  tableWrapperWidth: number,
) => boolean;

const useShouldBreakTable = (): useShouldBreakTableReturnFunc => {
  const breakpoint = useRef<number | undefined>();

  return (tableWidth: number, tableWrapperWidth: number) =>
    shouldUseMobileView(tableWidth, tableWrapperWidth, breakpoint);
};

export const Table: React.FC<React.PropsWithChildren<TableWrapperProps>> = ({
  children,
  className,
  id,
  getInitTableWrapperScroll,
  tableWrapperOnScroll,
  tableWrapperClassName,
  maxNoElements,
  numHeaders,
  blankOverflow,
  tableMainScrollTop,
}) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [breakTable, setBreakTable] = useState<boolean>(false);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [tableHeight, setTableHeight] = useState<number>(0);
  const [tableMaxHeight, setTableMaxHeight] = useState<number>(
    getMaxHeightForTable(maxNoElements),
  );
  const [wrapperHasMounted, setWrapperHasMounted] = useState(false);

  // useRef instead of useState -> want to store srollTop, but not cause rerender, when ref.current is mutated
  const scrollTop = useRef(0);

  useEffect(() => {
    if (tableWrapperRef.current) {
      setWrapperHasMounted(true);
    }
  }, [tableWrapperRef]);

  useEffect(() => {
    const initScroll = getInitTableWrapperScroll && getInitTableWrapperScroll();
    if (initScroll && wrapperHasMounted) {
      tableWrapperRef.current!.scroll({ top: initScroll });
    }
  }, [wrapperHasMounted, getInitTableWrapperScroll]);

  const shouldBreakTable = useShouldBreakTable();
  const handleWindowResize = useCallback(() => {
    if (tableRef.current && tableWrapperRef.current) {
      const tableWrapperWidth = tableWrapperRef.current.clientWidth;
      const tableWidth = tableRef.current.clientWidth;
      const shouldBreak = shouldBreakTable(tableWidth, tableWrapperWidth);

      setBreakTable(shouldBreak);

      const maxHeight = getMaxHeightForTable(
        maxNoElements,
        shouldBreak && numHeaders ? numHeaders : undefined,
      );
      if (tableMaxHeight !== maxHeight) setTableMaxHeight(maxHeight);
    }
  }, [
    tableRef,
    tableWrapperRef,
    tableMaxHeight,
    maxNoElements,
    numHeaders,
    shouldBreakTable,
  ]);

  const handleHeightChange = useCallback(() => {
    if (tableRef?.current) {
      const height = tableRef.current.clientHeight;

      if (height !== tableHeight) setTableHeight(height);
    }
  }, [tableRef, tableHeight]);

  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current && tableRef.current) {
      handleWindowResize();
      handleHeightChange();
      hasMounted.current = true;
    }
  }, [handleWindowResize, handleHeightChange, hasMounted, tableRef]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);

    // Actually checks for changes in e.g. no. children, which will inherently have an effect on height
    const heightObserver = new MutationObserver(handleHeightChange);
    if (tableRef?.current)
      heightObserver.observe(tableRef.current, {
        childList: true,
        subtree: true,
      });

    return () => window.removeEventListener('resize', handleWindowResize);
  }, [handleWindowResize, handleHeightChange]);

  const onScrollHandler = (event: React.UIEvent) => {
    if (!isScrolling) {
      setIsScrolling(true);
    }

    scrollTop.current = event.currentTarget.scrollTop;
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isScrolling && tableWrapperOnScroll) {
        setIsScrolling(false);
        tableWrapperOnScroll(scrollTop.current);
      }
    }, 250);

    return () => {
      clearTimeout(timeout);
    };
  }, [isScrolling, scrollTop, tableWrapperOnScroll]);

  const tableContext: TableContextProps = useMemo(() => {
    const common = {
      breakTable,
      maxHeight: tableMaxHeight,
      tableMainScrollTop: 0,
    };

    if (
      numHeaders !== undefined &&
      tableMainScrollTop !== undefined &&
      blankOverflow !== undefined
    ) {
      return {
        ...common,
        numHeaders,
        blankOverflow,
        tableMainScrollTop,
      };
    }

    return common;
  }, [
    breakTable,
    numHeaders,
    tableMaxHeight,
    tableMainScrollTop,
    blankOverflow,
  ]);

  return (
    <div
      className={classNames(
        css.tableWrapper,
        tableWrapperClassName,
        breakTable && css.breakTable,
        tableHeight >= tableMaxHeight && css.overflow,
      )}
      style={{ maxHeight: tableMaxHeight }}
      ref={tableWrapperRef}
      onScroll={onScrollHandler}
    >
      <TableContext.Provider value={tableContext}>
        <table
          id={id}
          className={classNames(css.table, className ? className : '')}
          ref={tableRef}
        >
          {children}
        </table>
      </TableContext.Provider>
    </div>
  );
};

export const TableHeader: React.FC<React.PropsWithChildren<TableProps>> = ({
  children,
  className,
  id,
}) => {
  return (
    <thead
      id={id}
      className={classNames(css.tableHeader, className ? className : '')}
    >
      {children}
    </thead>
  );
};

export const TableMain: React.FC<React.PropsWithChildren<TableProps>> = ({
  children,
  className,
  id,
}) => {
  const { blankOverflow } = useContext(TableContext);

  return blankOverflow ? (
    <TableMainWithBlankOverflow {...{ className, id, children }} />
  ) : (
    <tbody id={id} className={classNames(css.tableMain, className)}>
      {children}
    </tbody>
  );
};

const ROWS_TO_SHOW_MULTIPLIER = 4;

const TableMainWithBlankOverflow = React.memo<TableProps>(
  ({ id, className, children }) => {
    const { breakTable, numHeaders, maxHeight, tableMainScrollTop } =
      useContext(TableContext);
    const [offset, setOffset] = useState(Math.round(tableMainScrollTop));

    const childrenAsArray = React.Children.toArray(children);
    const numRows = useMemo(() => childrenAsArray.length, [childrenAsArray]);

    const cellHeight = useMemo(
      () =>
        breakTable && numHeaders
          ? DEFAULT_CELL_HEIGHT_BROKEN_TABLE * numHeaders
          : DEFAULT_CELL_HEIGHT,
      [breakTable, numHeaders],
    );

    useEffect(() => {
      const timer = setTimeout(() => {
        if (
          offset !== tableMainScrollTop &&
          Math.abs(offset - tableMainScrollTop) > maxHeight * 2
        )
          setOffset(Math.round(tableMainScrollTop));
      }, 250);

      return () => {
        clearTimeout(timer);
      };
    }, [tableMainScrollTop, offset, maxHeight]);

    const numRowsOffset = useMemo(
      () => Math.round(offset / cellHeight),
      [offset, cellHeight],
    );
    const numRowsShown = useMemo(
      () => Math.round(maxHeight / cellHeight),
      [maxHeight, cellHeight],
    );

    const rowsToRender = useMemo(() => {
      const blankRowsTo = roundToNearestEvenNumber(
        numRowsOffset > numRowsShown * ROWS_TO_SHOW_MULTIPLIER
          ? numRowsOffset - numRowsShown * ROWS_TO_SHOW_MULTIPLIER
          : 0,
      );

      const blankRowsFrom = roundToNearestEvenNumber(
        numRows > numRowsShown * ROWS_TO_SHOW_MULTIPLIER + numRowsOffset
          ? numRowsShown * ROWS_TO_SHOW_MULTIPLIER + numRowsOffset
          : numRows - 1,
      );

      const updatedChildren = childrenAsArray.slice(
        blankRowsTo,
        blankRowsFrom + 1,
      );

      const topBlankRowHeight = blankRowsTo > 0 ? blankRowsTo * cellHeight : 0;

      const bottomBlankRowHeight =
        blankRowsFrom < numRows - 1
          ? (numRows - blankRowsFrom - 1) * cellHeight
          : 0;

      return (
        <>
          <tr
            className={css.blank}
            style={{
              height: `${topBlankRowHeight}px`,
            }}
            aria-hidden
          />
          {updatedChildren}
          <tr
            className={css.blank}
            style={{
              height: `${bottomBlankRowHeight}px`,
            }}
            aria-hidden
          />
        </>
      );
    }, [childrenAsArray, numRowsOffset, numRowsShown, cellHeight, numRows]);

    return (
      <tbody
        id={id}
        className={classNames(css.tableMain, className ? className : '')}
      >
        {rowsToRender}
      </tbody>
    );
  },
);

export const TableRow: React.FC<React.PropsWithChildren<TableRowType>> = ({
  children,
  className,
  top,
  rowBackground,
  noActiveHeaders,
  id,
  ...props
}) => {
  const getRowBackground = () => {
    switch (rowBackground) {
      case 'alert':
        return css.alert;
      case 'shade':
        return css.shade;
      case 'sky':
        return css.sky;
      case 'deactivated':
        return css.deactivated;
    }
    return '';
  };

  return (
    <tr
      {...props}
      id={id}
      className={classNames(
        css.tableRow,
        className ? className : '',
        top ? css.top : getRowBackground(),
        noActiveHeaders ? css.noActiveHeaders : '',
      )}
    >
      {children}
    </tr>
  );
};

export const TableCellTd = ({
  id,
  className,
  align,
  header,
  children,
  ...props
}: TableCellTdProps) => {
  const { breakTable } = useContext(TableContext);
  const style = align && !breakTable ? { textAlign: align } : undefined;

  return (
    <td
      {...props}
      id={id}
      className={classNames(css.tableCell, className)}
      style={style}
      data-th={header}
    >
      {children}
    </td>
  );
};

/** IMPORTANT: Sorting and searching does not work, when the parent table is
 * in mobile view - cells are also removed from the focus order in that case */
export const TableCellTh = (props: TableCellThProps) =>
  props.canSort || props.canSearch ? (
    <TableCellTopSortableSearchable {...props} />
  ) : (
    <TableCellTopGeneric {...props} />
  );

const TableCellTopSortableSearchable = ({
  children,
  className,
  sort,
  activeCol,
  setActiveCol,
  colIndex,
  canSort,
  align,
  id,
  canSearch,
  search,
  initAscending,
  ...props
}: TableCellThProps) => {
  const { breakTable } = useContext(TableContext);
  const [ascending, setAscending] = useState<boolean>(!!initAscending);
  const [searchFieldActive, setSearchFieldActive] = useState<boolean>(false);

  const handleClickOrKeyDown = () => {
    if ((canSort || canSearch) && setActiveCol && colIndex !== undefined) {
      setActiveCol(colIndex);

      if (canSort && sort) {
        sort(colIndex, activeCol ? !ascending : ascending);
        if (activeCol) setAscending(!ascending);
      }
    }
  };

  const onSearchChange = (value: string) => {
    if (colIndex !== undefined && search) search(colIndex, value);
  };

  useEffect(() => {
    if (!activeCol) setAscending(false);
  }, [activeCol]);

  const searchFieldOnFocus = () => setSearchFieldActive(true);
  const searchFieldOnBlur = () => setSearchFieldActive(false);

  const activeSort = canSort && sort && activeCol;

  const getAriaSort = (): 'ascending' | 'descending' | undefined => {
    if (activeSort) {
      return ascending ? 'ascending' : 'descending';
    }
  };

  const colName = children?.toString() || '';
  const style = align && !breakTable ? { textAlign: align } : undefined;

  return (
    <th
      {...props}
      id={id}
      className={classNames(
        css.tableCell,
        !breakTable && setActiveCol && css.clickable,
        (activeCol || searchFieldActive) && css.active,
        className,
      )}
      style={style}
      aria-sort={getAriaSort()}
    >
      {canSearch && search ? (
        <SearchField
          id={`${id ? `${id}-` : ''}${colIndex?.toString()}` || ''}
          search={onSearchChange}
          placeholder={colName}
          activeIcon={activeCol || searchFieldActive}
          onBlur={searchFieldOnBlur}
          onFocus={searchFieldOnFocus}
          onClick={handleClickOrKeyDown}
          colName={colName}
        />
      ) : (
        children
      )}
      <SortArrows
        canSort={canSort}
        activeSort={activeSort}
        ascending={ascending}
        onClick={handleClickOrKeyDown}
        colName={colName}
      />
    </th>
  );
};

const TableCellTopGeneric = ({
  children,
  className,
  align,
  id,
  /* Props below not used - but specified to be omitted from spread */
  sort,
  activeCol,
  setActiveCol,
  colIndex,
  canSort,
  canSearch,
  search,
  initAscending,
  /* Props above not used - but specified to be omitted from spread */
  ...props
}: TableCellThProps) => {
  const { breakTable } = useContext(TableContext);
  const style = align && !breakTable ? { textAlign: align } : undefined;

  return (
    <th
      {...props}
      id={id}
      className={classNames(css.tableCell, className ? className : '')}
      style={style}
    >
      {children}
    </th>
  );
};

interface SortArrowsProps {
  ascending: boolean;
  onClick: () => void;
  activeSort: boolean | undefined;
  canSort: boolean | undefined;
  colName: string;
}

const SortArrows = ({
  ascending,
  onClick,
  canSort,
  activeSort,
  colName,
}: SortArrowsProps) => {
  const { sortColumn } = useFormatMessageFor({
    sortColumn: { ...translations.sortColumn, values: { colName } },
  });

  const { breakTable } = useContext(TableContext);

  return canSort ? (
    <button
      className={css.sortArrows}
      onClick={onClick}
      tabIndex={breakTable ? undefined : 0}
      aria-label={sortColumn}
    >
      {activeSort && (
        <div className={css.arrowWrapper}>
          <div className={classNames(css.arrow, ascending && css.active)}>
            <Icon type={IconType.CloseRowTriangle} title="" />
          </div>
          <div className={classNames(css.arrow, !ascending && css.active)}>
            <Icon type={IconType.OpenRowTriangle} title="" />
          </div>
        </div>
      )}
    </button>
  ) : null;
};

interface SearchFieldProps {
  id: string;
  search: (value: string) => void;
  placeholder: string;
  activeIcon?: boolean;
  onClick: () => void;
  onFocus: () => void;
  onBlur: () => void;
  colName: string;
}

const SearchField = ({
  id,
  search,
  placeholder,
  activeIcon,
  onClick,
  onBlur,
  onFocus,
  colName,
}: SearchFieldProps) => {
  const { searchColumn } = useFormatMessageFor({
    searchColumn: { ...translations.searchColumn, values: { colName } },
  });

  const [value, setValue] = useState<string>('');

  const onChange = (searchValue: string) => {
    setValue(searchValue);
    search(searchValue);
  };

  return (
    <div
      className={classNames(
        css.searchFieldWrapper,
        css.icon,
        activeIcon ? css.activeIcon : css.inactiveIcon,
      )}
    >
      <Icon className={css.searchIcon} type={IconType.Search} title="" />
      <TextInput
        containerClassName={css.container}
        inputClassName={css.input}
        labelClassName={css.inputLabel}
        ariaLabel={searchColumn}
        htmlId={`table-search-${id}`}
        onChange={onChange}
        value={value}
        placeholder={placeholder}
        autoComplete={'off'}
        onClick={onClick}
        onFocus={onFocus}
        onBlur={onBlur}
        hideBorder
      />
    </div>
  );
};
